import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ExternalLink } from "lucide-react";
import CopyField from "@/components/CopyField";
import { mobileDigits } from "@/lib/phone";
import ImageUploader from "@/components/ImageUploader";

const inputClass = "w-full mt-1 border border-neutral-300 px-3 py-2.5 text-sm focus:outline-none focus:border-black";
const cardClass = "bg-white border border-neutral-200 p-4 sm:p-6 min-w-0";
const primaryBtn = "bg-black text-white px-5 py-2.5 text-xs font-semibold uppercase tracking-wider hover:bg-neutral-800 disabled:opacity-50 min-h-[44px]";
const secondaryBtn = "border border-neutral-300 px-4 py-2.5 text-xs font-semibold uppercase tracking-wider hover:bg-neutral-50 min-h-[44px]";

// Same rule as mobile/build_app.py: the default application ID for a tenant.
const defaultAppId = (slug) => {
  let segment = slug.toLowerCase().replace(/[^a-z0-9]/g, "");
  if (!segment || /^\d/.test(segment)) segment = `t${segment}`;
  return `in.localappstore.services.${segment}`;
};

// Every tenant has all three from the moment it is created (its own default
// text). Text saved here replaces the default for this tenant only.
const LEGAL_DOCS = [
  { doc: "privacy-policy", field: "privacy_policy", label: "Privacy Policy" },
  { doc: "terms", field: "terms", label: "Terms & Conditions" },
  { doc: "account-deletion", field: "account_deletion", label: "Account Deletion" },
];
const emptyLegal = Object.fromEntries(LEGAL_DOCS.map(({ field }) => [field, ""]));

/**
 * Everything the Super Admin manages for ONE tenant. Every request on this page
 * carries this tenant's id, so nothing here can touch another tenant.
 */
export default function SuperTenantDetail() {
  const { tenantId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [identity, setIdentity] = useState({ business_name: "", app_name: "", tagline: "", logo_url: "" });
  const [legalText, setLegalText] = useState(emptyLegal);
  const [saving, setSaving] = useState(null);
  const [adminPhone, setAdminPhone] = useState("");

  const apply = useCallback((d) => {
    setData(d);
    const b = d.tenant.branding || {};
    setIdentity({ business_name: d.tenant.business_name || "", app_name: b.app_name || "", tagline: b.tagline || "", logo_url: b.logo_url || "" });
    setLegalText(Object.fromEntries(LEGAL_DOCS.map(({ field }) => [field, d.tenant.legal?.[field] || ""])));
    setAdminPhone(mobileDigits(d.admins?.[0]?.phone));
  }, []);

  const load = useCallback(() => {
    setData(null); setError(null);
    api.get(`/super/tenants/${tenantId}`).then((r) => apply(r.data)).catch((e) => setError(e?.response?.data?.detail || "Could not load tenant"));
  }, [tenantId, apply]);
  useEffect(() => { load(); }, [load]);

  if (error) return <div className="bg-white border border-neutral-200 p-8 text-sm text-neutral-600" data-testid="super-tenant-error">{error}. <Link to="/super/tenants" className="underline">Back to tenants</Link></div>;
  if (!data) return <div className="text-sm text-neutral-500">Loading…</div>;

  const { tenant, admins = [], test_admins: testAdmins = [], legal } = data;
  const admin = admins[0];
  const origin = window.location.origin;
  const tenantUrl = `${origin}/t/${tenant.slug}`;
  const mobile = tenant.mobile_app || {};

  const saveIdentity = async (e) => {
    e.preventDefault();
    setSaving("identity");
    try {
      await api.put(`/super/tenants/${tenant.id}/identity`, identity);
      toast.success(`Saved for ${identity.business_name}`);
      const r = await api.get(`/super/tenants/${tenant.id}`); apply(r.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save — check the business name");
    } finally { setSaving(null); }
  };

  // An uploaded logo that is not saved never reaches the tenant's website or
  // its Android and iOS apps, so it is saved the moment it is uploaded or
  // cleared. Only the logo is sent: name edits still wait for Save.
  const saveLogo = async (url) => {
    const before = identity.logo_url;
    setIdentity((i) => ({ ...i, logo_url: url }));
    try {
      await api.put(`/super/tenants/${tenant.id}/identity`, { logo_url: url });
      toast.success(url ? `Logo saved for ${tenant.business_name}` : `Logo removed for ${tenant.business_name}`);
    } catch (err) {
      setIdentity((i) => ({ ...i, logo_url: before }));
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "The logo could not be saved");
    }
  };

  const saveLegal = async (field, value) => {
    setSaving(field);
    try {
      await api.put(`/super/tenants/${tenant.id}/legal`, { [field]: value });
      toast.success(value.trim() ? "Saved" : "Reset to this tenant's default text");
      const r = await api.get(`/super/tenants/${tenant.id}`); apply(r.data);
    } catch { toast.error("Could not save"); } finally { setSaving(null); }
  };

  const saveAdminPhone = async (e) => {
    e.preventDefault();
    if (admin?.phone && !window.confirm(`Change the admin sign-in number for ${tenant.business_name}? The current number stops working.`)) return;
    setSaving("admin");
    try {
      const r = await api.put(`/super/tenants/${tenant.id}/admin`, { phone: adminPhone });
      setData({ ...data, admins: [r.data.admin, ...admins.slice(1)] });
      setAdminPhone(mobileDigits(r.data.admin.phone));
      toast.success("Admin sign-in number saved");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Enter a valid mobile number");
    } finally { setSaving(null); }
  };

  return (
    <div data-testid="super-tenant-detail">
      <Link to="/super/tenants" className="text-xs font-semibold uppercase tracking-wider text-neutral-600 hover:text-black inline-flex items-center gap-1.5 min-h-[44px]">
        <ArrowLeft className="h-3.5 w-3.5" /> All tenants
      </Link>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div className="min-w-0">
          <div className="admin-overline break-anywhere">Tenant · {tenant.slug} · {tenant.status}</div>
          <h1 className="admin-heading text-3xl font-bold mt-1 break-words" data-testid="super-tenant-detail-name">{tenant.business_name}</h1>
        </div>
        <a href={tenantUrl} target="_blank" rel="noreferrer" className={`${secondaryBtn} bg-white inline-flex items-center gap-2`}>
          Open customer app <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>

      <div className="grid xl:grid-cols-2 gap-4 sm:gap-6">
        {/* Name and logo */}
        <form onSubmit={saveIdentity} className={`${cardClass} space-y-4`} data-testid="super-tenant-identity">
          <div className="admin-overline">Name and logo</div>
          <div>
            <label className="admin-overline" htmlFor="tenant-business-name">Business name</label>
            <input id="tenant-business-name" required maxLength={80} value={identity.business_name}
              onChange={(e) => setIdentity({ ...identity, business_name: e.target.value })} className={inputClass} data-testid="super-tenant-business-name" />
          </div>
          <div>
            <label className="admin-overline" htmlFor="tenant-app-name">App name</label>
            <input id="tenant-app-name" maxLength={60} value={identity.app_name} placeholder={identity.business_name}
              onChange={(e) => setIdentity({ ...identity, app_name: e.target.value })} className={inputClass} data-testid="super-tenant-app-name" />
            <div className="text-[11px] text-neutral-500 mt-1">Shown under the app icon and in the customer app. Leave blank to use the business name.</div>
          </div>
          <div>
            <label className="admin-overline" htmlFor="tenant-tagline">Tagline</label>
            <input id="tenant-tagline" maxLength={140} value={identity.tagline} onChange={(e) => setIdentity({ ...identity, tagline: e.target.value })} className={inputClass} />
          </div>
          <ImageUploader
            label="Logo / app icon (square PNG, 1024×1024 recommended)"
            value={identity.logo_url}
            onChange={saveLogo}
            folder="branding/logo" tenantSlug={tenant.slug} shape="square" testid="super-tenant-logo"
          />
          <div className="text-[11px] text-neutral-500">Upload a new image to replace the current logo, or clear it. It is saved at once, and used by this tenant's website and by the next build of its Android and iOS apps. The file is stored under this tenant only.</div>
          <button type="submit" disabled={saving === "identity"} className={primaryBtn} data-testid="super-tenant-identity-save">
            {saving === "identity" ? "Saving..." : "Save name and logo"}
          </button>
        </form>

        {/* Slug, URLs and store details */}
        <div className={`${cardClass} space-y-4`} data-testid="super-tenant-links">
          <div className="admin-overline">Slug, links and store listing</div>
          <CopyField label="Tenant slug" value={tenant.slug} testid="super-tenant-slug" />
          <CopyField label="Tenant URL" value={tenantUrl} href={tenantUrl} testid="super-tenant-url" />
          <CopyField label="Privacy Policy URL" value={`${tenantUrl}/privacy-policy`} href={`${tenantUrl}/privacy-policy`} testid="super-tenant-privacy-url"
            hint="Use this in Google Play Console and App Store Connect." />
          <CopyField label="Terms & Conditions URL" value={`${tenantUrl}/terms`} href={`${tenantUrl}/terms`} testid="super-tenant-terms-url" />
          <CopyField label="Account Deletion URL" value={`${tenantUrl}/account-deletion`} href={`${tenantUrl}/account-deletion`} testid="super-tenant-delete-url"
            hint="Google Play and the App Store ask for a web page that explains how customers delete their account. This page needs no sign-in and links to the delete step." />
          <CopyField label="App name" value={tenant.branding?.app_name || tenant.business_name} testid="super-tenant-store-name" />
          <CopyField label="Android application ID" value={mobile.android_package_name || defaultAppId(tenant.slug)} testid="super-tenant-android-id"
            hint={mobile.android_package_name ? "Set under Mobile Apps." : "Default. Set a different one under Mobile Apps before the first store upload."} />
          <CopyField label="iOS bundle ID" value={mobile.ios_bundle_id || defaultAppId(tenant.slug)} testid="super-tenant-ios-id"
            hint={mobile.ios_bundle_id ? "Set under Mobile Apps." : "Default. Set a different one under Mobile Apps before the first store upload."} />
          <CopyField label="Tenant admin login URL" value={`${tenantUrl}/admin/login`} href={`${tenantUrl}/admin/login`} testid="super-tenant-admin-url" />
        </div>

        {/* Legal documents */}
        {LEGAL_DOCS.map(({ doc, field, label }) => {
          const current = legal[doc];
          return (
            <div key={doc} className={`${cardClass} space-y-3`} data-testid={`super-tenant-legal-${doc}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="admin-overline">{label}</div>
                <span className="status-pill" style={current.is_default ? { background: "#F5F5F5", color: "#525252" } : { background: "#DCFCE7", color: "#166534" }}>
                  {current.is_default ? "Default text" : "Custom text"}
                </span>
              </div>
              <p className="text-xs text-neutral-600">
                {current.is_default
                  ? `Nothing to set up: the page already shows a default written with ${tenant.business_name}'s own name and contact details. Save text here only if this business needs its own wording.`
                  : `Custom text saved${current.updated_at ? ` on ${current.updated_at.slice(0, 10)}` : ""}. Only ${tenant.business_name} shows it; every other tenant keeps its own.`}
              </p>
              <textarea rows={10} value={legalText[field]} onChange={(e) => setLegalText({ ...legalText, [field]: e.target.value })}
                placeholder={`Paste or write ${tenant.business_name}'s ${label.toLowerCase()}. Start a line with "## " for a heading.`}
                className={`${inputClass} font-mono text-xs leading-relaxed`} data-testid={`super-tenant-legal-${doc}-text`} />
              <div className="flex flex-wrap gap-2">
                <button type="button" disabled={saving === field} onClick={() => saveLegal(field, legalText[field])} className={primaryBtn} data-testid={`super-tenant-legal-${doc}-save`}>
                  {saving === field ? "Saving..." : "Save"}
                </button>
                {current.is_default && !legalText[field] && (
                  <button type="button" onClick={() => setLegalText({ ...legalText, [field]: current.content })} className={secondaryBtn}>Start from the default text</button>
                )}
                {!current.is_default && (
                  <button type="button" onClick={() => saveLegal(field, "")} className={secondaryBtn} data-testid={`super-tenant-legal-${doc}-reset`}>Reset to default</button>
                )}
              </div>
            </div>
          );
        })}

        {/* Tenant admin access */}
        <div className={`${cardClass} space-y-3 xl:col-span-2`} data-testid="super-tenant-admin-access">
          <div className="admin-overline">Tenant admin access</div>
          <p className="text-sm text-neutral-600 max-w-2xl">
            The admin of this business signs in with a mobile number and a one-time code sent to it by SMS or WhatsApp. There is no password.
            {admin?.phone ? "" : " No number is set yet, so nobody can sign in to this business's admin console."}
          </p>
          <form onSubmit={saveAdminPhone} className="flex flex-wrap items-end gap-2" data-testid="super-tenant-admin-form">
            <div className="flex-1 min-w-[220px] max-w-sm">
              <label className="admin-overline" htmlFor="tenant-admin-phone">Admin mobile number</label>
              <input id="tenant-admin-phone" type="tel" inputMode="numeric" required pattern="[6-9][0-9]{9}" title="10-digit mobile number, without +91" value={adminPhone}
                onChange={(e) => setAdminPhone(mobileDigits(e.target.value))}
                className={`${inputClass} font-mono`} placeholder="98XXXXXXXX" data-testid="super-tenant-admin-phone" />
              <div className="text-[11px] text-neutral-500 mt-1">10 digits, without +91.</div>
            </div>
            <button type="submit" disabled={saving === "admin" || adminPhone.length !== 10 || adminPhone === mobileDigits(admin?.phone)} className={secondaryBtn} data-testid="super-tenant-admin-save">
              {saving === "admin" ? "Saving..." : admin?.phone ? "Change number" : "Set number"}
            </button>
          </form>
          {admin?.email && <div className="text-xs text-neutral-500 break-anywhere">Contact email on record: {admin.email}</div>}
          {testAdmins.length > 0 && (
            <div className="text-xs text-neutral-500 break-anywhere" data-testid="super-tenant-test-admin">
              Development test admin for this tenant: {testAdmins.map((t) => t.phone).join(", ")} (fixed code; exists only in test mode).
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
